import loadJS from 'loadjs';

const CLOUDINARY_JS_URL = 'https://widget.cloudinary.com/v2.0/global/all.js';

export type UploaderPluginTypes = {
    cloud_name: string;
    upload_preset: string;
    croppingAspectRatio: number;
    maxImageWidth: number;
    showSkipCropButton: boolean;
    cropping: 'server' | boolean;
    transformation?: string;
    clientAllowedFormats?: Array<string>;
    sources?: Array<string>;
    resourceType?: string;
    style?: {
        palette?: {
            window: string;
            sourceBg: string;
            windowBorder: string;
            tabIcon: string;
            inactiveTabIcon: string;
            menuIcons: string;
            link: string;
            action: string;
            inProgress: string;
            complete: string;
            error: string;
            textDark: string;
            textLight: string;
        };
    };
    onWidgetShown?: () => void;
};

type BatchCancelledEvent = {
    event: 'batch-cancelled';
    info: {
        reason: string;
        original_filename: string;
        format: string;
        secure_url: string;
    };
};

type DisplayChangedEvent = {
    event: 'display-changed';
    info: 'shown' | 'hidden' | 'minimized' | 'expanded';
};

type SuccessEvent = {
    event: 'success';
};

type CloseEvent = {
    event: 'close';
};

type ResultTypes = SuccessEvent | CloseEvent | DisplayChangedEvent | BatchCancelledEvent;

const uploaderPlugin = ({ onWidgetShown, ...options }: UploaderPluginTypes) => {
    const initialize = () => {
        if (window?.['cloudinary']?.['openUploadWidget']) {
            return;
        }
        const script = document.querySelector(`script[src="${CLOUDINARY_JS_URL}"]`);
        script && script?.parentNode?.removeChild(script);

        return loadJS(CLOUDINARY_JS_URL, { returnPromise: true });
    };

    const upload = async () => {
        await initialize();

        return new Promise<ResultTypes>((resolve, reject) => {
            window?.['cloudinary']?.['openUploadWidget'](
                options,
                (error: Error, result: ResultTypes) => {
                    if (result.event === 'close') {
                        return reject(new Error('User canceled upload'));
                    }
                    if (error) {
                        return reject(error);
                    } else if (result.event === 'success') {
                        return resolve(result);
                    } else if (result.event === 'batch-cancelled') {
                        return reject(new Error(result.info.reason));
                    } else if (result.event === 'display-changed' && result.info === 'shown') {
                        onWidgetShown?.();
                    }
                }
            );
        });
    };

    return async () => {
        const res = await upload();
        const { original_filename, format, secure_url } = res?.['info'] || {};
        const arr = secure_url.split('upload');
        const croppedImageUrl = `${arr[0]}upload/${options.transformation}${arr[1]}`;

        return {
            originalFilename: `${original_filename}.${format}`,
            originalResponse: res,
            plugin: 'UploaderPluginCloudinary',
            originUrl: secure_url,
            url: croppedImageUrl,
        };
    };
};

export default uploaderPlugin;
